import * as React from 'react';
import { styled } from '@linaria/react';

import useTickSfx from '@/hooks/use-tick-sfx';

import Demo, { DemoSliderControl } from '@/components/Demo';
import Space from '@/components/Space';

import {
  POSITION_RADIUS,
  Svg,
  Label,
  PositionDot,
  CodeSnippet,
  Indicators,
} from './BasicShapes.shared';
import { ExplodedState } from './BasicShapes.types';
import GuideLine from './GuideLine';
import ExplodedLine from './ExplodedLine';
import ExplodedExplanation from './ExplodedExplanation';

const SIZE = 280;

function BasicLineDemo() {
  const [explodedState, setExplodedState] =
    React.useState<ExplodedState>('peace');
  const isExploded = explodedState === 'exploded';

  const playTick = useTickSfx();

  // Ok, ugh, so I need to set explodedState to 'exploded' when x1/y1 is the same as x2/y2. This state is internal to Demo and I don’t really want to manage this state up here, since it uses the activeControlId stuff.
  // So instead, I’ll copy that state into refs on change, and use the refs in my calculations.
  const stateRef = React.useRef<{
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  }>({ x1: 80, y1: 80, x2: SIZE - 80, y2: SIZE - 80 });

  function checkForExplosion() {
    if (
      explodedState === 'peace' &&
      stateRef.current.x1 === stateRef.current.x2 &&
      stateRef.current.y1 === stateRef.current.y2
    ) {
      setExplodedState('exploded');
    }
  }

  return (
    <Demo
      id="basic-line-demo"
      outputWrapper={OutputWrapper}
      initialValues={{
        x1: 80,
        y1: 80,
        x2: SIZE - 80,
        y2: SIZE - 80,
      }}
      CustomGridComponent={ControlsGrid}
      controls={[
        <DemoSliderControl
          highlightControlWhenActive
          key="x1"
          id="x1"
          label="x1"
          min={0}
          max={SIZE}
          step={10}
          disabled={isExploded}
          wrapperStyle={{
            gridArea: 'x1',
          }}
          onChange={(val) => {
            stateRef.current.x1 = val;
            playTick();
            checkForExplosion();
          }}
        />,
        <DemoSliderControl
          highlightControlWhenActive
          key="y1"
          id="y1"
          label="y1"
          min={0}
          max={SIZE}
          step={10}
          disabled={isExploded}
          wrapperStyle={{
            gridArea: 'y1',
          }}
          onChange={(val) => {
            stateRef.current.y1 = val;
            playTick();
            checkForExplosion();
          }}
        />,
        <DemoSliderControl
          highlightControlWhenActive
          key="x2"
          id="x2"
          label="x2"
          min={0}
          max={SIZE}
          step={10}
          disabled={isExploded}
          wrapperStyle={{
            gridArea: 'x2',
          }}
          onChange={(val) => {
            stateRef.current.x2 = val;
            playTick();
            checkForExplosion();
          }}
        />,
        <DemoSliderControl
          highlightControlWhenActive
          key="y2"
          id="y2"
          label="y2"
          min={0}
          max={SIZE}
          step={10}
          disabled={isExploded}
          wrapperStyle={{
            gridArea: 'y2',
          }}
          onChange={(val) => {
            stateRef.current.y2 = val;
            playTick();
            checkForExplosion();
          }}
        />,
      ]}
    >
      {({ x1, y1, x2, y2 }, activeControlId) => {
        const hideIndicators = x1 === x2 && y1 === y2;

        const code = `\
<svg width="${SIZE}" height="${SIZE}">
  <line
    x1="${x1}"
    y1="${y1}"
    x2="${x2}"
    y2="${y2}"
    stroke="oklch(0.9 0.3 164)"
    stroke-width="5"
  />
</svg>\
`;

        return (
          <GridRow>
            <SvgWrapper>
              <Svg size={SIZE}>
                <Indicators
                  style={{
                    opacity: hideIndicators ? 0 : 1,
                  }}
                >
                  <GuideLine
                    x1={x1 - POSITION_RADIUS - 2}
                    y1={y1}
                    x2={0}
                    y2={y1}
                    data-is-active={activeControlId === 'x1'}
                  />
                  <GuideLine
                    x1={x1}
                    y1={y1 - POSITION_RADIUS - 2}
                    x2={x1}
                    y2={0}
                    data-is-active={activeControlId === 'y1'}
                  />
                  <GuideLine
                    x1={x2 - POSITION_RADIUS - 2}
                    y1={y2}
                    x2={0}
                    y2={y2}
                    data-is-active={activeControlId === 'x2'}
                  />
                  <GuideLine
                    x1={x2}
                    y1={y2 - POSITION_RADIUS - 2}
                    x2={x2}
                    y2={0}
                    data-is-active={activeControlId === 'y2'}
                  />

                  {/* MAIN SHAPE: */}
                  <line
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke="var(--wham-complementary-500)"
                    strokeWidth={5}
                  />

                  <Label
                    x={x1 / 2}
                    y={y1 + 20}
                    textAnchor="middle"
                    dominantBaseline="bottom"
                    data-is-hidden={x1 < 30}
                    data-is-active={activeControlId === 'x1'}
                  >
                    x1
                  </Label>
                  <Label
                    x={x1 + 30}
                    y={y1 / 2}
                    textAnchor="end"
                    dominantBaseline="middle"
                    data-is-hidden={y1 < 20}
                    data-is-active={activeControlId === 'y1'}
                  >
                    y1
                  </Label>
                  <Label
                    x={x2 / 2}
                    y={y2 + 20}
                    textAnchor="middle"
                    dominantBaseline="bottom"
                    data-is-hidden={x2 < 30}
                    data-is-active={activeControlId === 'x2'}
                  >
                    x2
                  </Label>
                  <Label
                    x={x2 + 30}
                    y={y2 / 2}
                    textAnchor="end"
                    dominantBaseline="middle"
                    data-is-hidden={y2 < 20}
                    data-is-active={activeControlId === 'y2'}
                  >
                    y2
                  </Label>

                  <PositionDot cx={x1} cy={y1} />
                  <PositionDot cx={x2} cy={y2} />
                </Indicators>
              </Svg>
              <ExplodedLine
                isExploded={isExploded}
                svgSize={SIZE}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
              />
            </SvgWrapper>

            <CodeWrapper>
              <CodeSnippet code={code} />
            </CodeWrapper>
            <ExplodedExplanation
              explodedState={explodedState}
              handleResetExplosion={() => {
                setExplodedState('cleanup');

                window.setTimeout(() => {
                  setExplodedState('aftermath');
                }, 1000);
              }}
            />
          </GridRow>
        );
      }}
    </Demo>
  );
}

const BREAKPOINT = '36rem';

const OutputWrapper = styled(Space)`
  container-type: inline-size;
  background: var(--color-background);
`;
const ControlsGrid = styled.div`
  display: grid;
  gap: 16px 32px;
  grid-template-areas:
    'x1'
    'y1'
    'x2'
    'y2';

  @media (min-width: 30rem) {
    grid-template-areas:
      'x1 y1'
      'x2 y2';
  }
  @media (min-width: 60rem) {
    grid-template-areas: 'x1 y1 x2 y2';
    gap: 16px 24px;
  }
`;

const GridRow = styled.div`
  display: grid;
  grid-template-areas: 'svg code';
  grid-template-columns: ${SIZE}px 1fr;
  gap: 16px;

  @container (max-width: ${BREAKPOINT}) {
    grid-template-areas:
      'code'
      'svg';
    grid-template-columns: 1fr;
    justify-items: center;
  }
`;

const SvgWrapper = styled.div`
  align-self: start;
  position: relative;
  display: flex;
  grid-area: svg;

  @container (max-width: ${BREAKPOINT}) {
    position: relative;
  }
`;

const CodeWrapper = styled.div`
  grid-area: code;
  min-width: 0;
  position: relative;
  display: flex;

  @container (max-width: ${BREAKPOINT}) {
    justify-self: stretch;
  }
`;

export default BasicLineDemo;
